/**
 * Le déroulé de l'écran de fin d'une activité : ce qui se montre, et quand. Pur.
 *
 * Celui de l'ancien `ActivityFinalizedCongrats`, au temps près :
 *  - à 500 ms, la tuile des XP apparaît (400 ms de pop, `CounterWrapper`) et ses XP défilent en 1 s
 *    (`XPCount`) ;
 *  - la tuile des pétales apparaît quand les XP ont fini (sans XP : à 500 ms), et ses pétales
 *    défilent en 1 s ;
 *  - le chrono apparaît quand la tuile d'avant a fini son pop (sans XP ni pétales : à 500 ms) ;
 *  - le bouton apparaît en fondu de 800 ms (`withTiming`, `Easing.out(Easing.ease)`) quand le dernier
 *    compteur a fini (sans rien à compter : tout de suite avec le chrono), puis son compte à rebours
 *    de 12 s le presse (`TouchableRippleM`, `autoClickDuration`). L'élève peut le presser avant.
 * Un écart, voulu : sans XP, l'ancien n'achevait jamais le compte des pétales, et le bouton ne
 * venait pas ; ici les pétales défilent quand même.
 *
 * En mouvement réduit, tout est là d'emblée, les nombres écrits, le bouton sans fondu : seul le
 * compte à rebours reste.
 */
import { Easing } from "react-native"

/** Le premier compteur part à 500 ms (`ANIMATION_START_DELAY`). */
export const REVEAL_START_MS = 500
/** Le pop d'une tuile. */
export const TILE_POP_MS = 400
/** Le défilement d'un nombre. */
export const COUNT_MS = 1000
/** Le fondu du bouton qui arrive. */
export const BUTTON_FADE_MS = 800
/** Le compte à rebours du bouton, à compter de son apparition (`COUNTDOWN_DURATION`) : `<CountdownButton>` le tient. */
export const AUTO_CONTINUE_MS = 12000

export type CongratsRevealPlan = {
    xpAt: number | null
    petalsAt: number | null
    chronoAt: number
    buttonAt: number
}

export function congratsRevealPlan(gains: { xp: number; petals: number }, reduceMotion = false): CongratsRevealPlan {
    const hasXp = gains.xp > 0
    const hasPetals = gains.petals > 0
    if (reduceMotion) return { xpAt: hasXp ? 0 : null, petalsAt: hasPetals ? 0 : null, chronoAt: 0, buttonAt: 0 }
    const xpAt = hasXp ? REVEAL_START_MS : null
    const petalsAt = hasPetals ? (xpAt != null ? xpAt + COUNT_MS : REVEAL_START_MS) : null
    const chronoAt = petalsAt != null ? petalsAt + TILE_POP_MS : xpAt != null ? xpAt + TILE_POP_MS : REVEAL_START_MS
    const buttonAt = petalsAt != null ? petalsAt + COUNT_MS : xpAt != null ? xpAt + COUNT_MS : chronoAt
    return { xpAt, petalsAt, chronoAt, buttonAt }
}

/** Le nombre affiché d'un compteur, `elapsed` ms après le début de l'écran. */
export function countedValue(target: number, startAt: number | null, elapsed: number, reduceMotion = false): number {
    if (startAt == null) return 0
    if (reduceMotion || elapsed >= startAt + COUNT_MS) return target
    if (elapsed <= startAt) return 0
    // Une montée qui ralentit au bout, comme le chiffre qui tourne de l'ancien compteur.
    const t = (elapsed - startAt) / COUNT_MS
    return Math.round(target * (1 - Math.pow(1 - t, 3)))
}

const fadeEasing = Easing.out(Easing.ease)

/** L'opacité d'un fondu qui part à `startAt`, `elapsed` ms après le début de l'écran. */
export function fadedIn(startAt: number, elapsed: number, reduceMotion = false): number {
    if (elapsed < startAt) return 0
    if (reduceMotion || elapsed >= startAt + BUTTON_FADE_MS) return 1
    return fadeEasing((elapsed - startAt) / BUTTON_FADE_MS)
}

export type CongratsRevealFrame = {
    xpVisible: boolean
    xp: number
    petalsVisible: boolean
    petals: number
    chronoVisible: boolean
    buttonVisible: boolean
    /** L'opacité du bouton : 0 avant lui, puis son fondu jusqu'à 1. */
    buttonOpacity: number
    /** Tout est montré : plus rien ne bouge, que le compte à rebours du bouton. */
    settled: boolean
}

export function congratsRevealFrame(
    gains: { xp: number; petals: number },
    elapsed: number,
    reduceMotion = false
): CongratsRevealFrame {
    const plan = congratsRevealPlan(gains, reduceMotion)
    const last = Math.max(plan.buttonAt, plan.chronoAt)
    return {
        xpVisible: plan.xpAt != null && elapsed >= plan.xpAt,
        xp: countedValue(gains.xp, plan.xpAt, elapsed, reduceMotion),
        petalsVisible: plan.petalsAt != null && elapsed >= plan.petalsAt,
        petals: countedValue(gains.petals, plan.petalsAt, elapsed, reduceMotion),
        chronoVisible: elapsed >= plan.chronoAt,
        buttonVisible: elapsed >= plan.buttonAt,
        buttonOpacity: fadedIn(plan.buttonAt, elapsed, reduceMotion),
        settled: elapsed >= last + (reduceMotion ? 0 : COUNT_MS),
    }
}
